// Panneau « Remise proposée » (décision du dirigeant du 31/10/2026) — DIRIGEANT seul (droit rentabilite.lire, option Ma gamme).
// Affiché à la fin de « Tout préparer » (écran d'estimation) et sur tout devis brouillon qui contient une ligne Ma gamme.
// Rien n'est appliqué d'office : un clic sur un taux (ou un taux saisi) applique LA remise du devis ; « Retirer la remise » la remet à
// zéro. Pour chaque taux : total HT après remise et marge restante sur le COÛT TOTAL du devis (même chaîne que l'onglet Rentabilité),
// calculés par le serveur ; un taux qui ferait vendre sous le coût (ou sous le seuil réglé) est grisé — et refusé par le serveur.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgePercent, Settings2, Undo2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { euros, nombre, pluriel } from '../../lib/format.ts';
import { Alerte, Bouton, Champ, Panneau, useNotifier } from '../../components/ui.tsx';
import {
  ASSIETTE_PHRASE, MARGE_MIN_MAX, pctFr, TAUX_REMISE_MAX, TAUX_REMISE_MIN, TAUX_REMISE_NB_MAX,
  type AssietteRemise, type EtatRemiseProposee, type ReglagesRemiseProposee,
} from '@shared/remise-proposee.ts';

/** Liste « 3 ; 5 ; 10 ; 15 » (séparateurs ; , espace) → taux en chaînes ; null si un taux est mal écrit (le serveur reste juge). */
export function lireListeTaux(s: string): string[] | null {
  const morceaux = s.split(/[;\s]+/).map((x) => x.trim().replace('%', '').replace(',', '.')).filter(Boolean);
  if (!morceaux.length || morceaux.length > TAUX_REMISE_NB_MAX) return null;
  if (morceaux.some((x) => !/^\d{1,2}(\.\d{1,2})?$/.test(x) || Number(x) < TAUX_REMISE_MIN || Number(x) > TAUX_REMISE_MAX)) return null;
  return morceaux;
}

export function PanneauRemiseProposee({ revisionId, apres }: { revisionId: string; apres?: () => void | Promise<void> }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [assiette, setAssiette] = useState<AssietteRemise | null>(null);
  const [saisie, setSaisie] = useState('');
  const cle = ['remise-proposee', revisionId, assiette];
  const q = useQuery({ queryKey: cle, queryFn: () => api.get<EtatRemiseProposee>(`/api/devis/revisions/${revisionId}/remise-proposee${assiette ? `?assiette=${assiette}` : ''}`) });
  const e = q.data;
  const choisie = assiette ?? e?.assiette ?? 'ma_gamme';
  const fini = async (x: EtatRemiseProposee, message: string) => {
    qc.setQueryData(['remise-proposee', revisionId, x.assiette], x);
    await qc.invalidateQueries({ queryKey: ['remise-proposee', revisionId] });
    await apres?.();
    notifier(message);
  };
  const appliquer = useMutation({
    mutationFn: (pct: string) => api.post<EtatRemiseProposee>(`/api/devis/revisions/${revisionId}/remise-proposee`, { pct, assiette: choisie }),
    onSuccess: (x, pct) => { setSaisie(''); return fini(x, `Remise de ${pctFr(pct)} appliquée ${ASSIETTE_PHRASE[choisie]}.`); },
    onError: (err) => notifier(messageErreur(err), true),
  });
  const retirer = useMutation({
    mutationFn: () => api.del<EtatRemiseProposee>(`/api/devis/revisions/${revisionId}/remise-proposee`),
    onSuccess: (x) => fini(x, 'Remise retirée.'),
    onError: (err) => notifier(messageErreur(err), true),
  });
  if (q.isError) return null;   // 403 (autre rôle, option inactive) ou devis figé : rien à proposer ici
  const saisieOk = /^\d{1,2}([.,]\d{1,2})?$/.test(saisie.trim()) && Number(saisie.replace(',', '.')) > 0 && Number(saisie.replace(',', '.')) <= TAUX_REMISE_MAX;
  const base = choisie === 'ma_gamme' ? e?.base_gamme : e?.ht_brut;
  return (
    <Panneau titre="Remise proposée" id="remise-proposee" className="rp-panneau">
      {!e ? <p className="petit discret">Calcul des remises proposées…</p> : (
        <div className="pile">
          <p className="petit secondaire">
            Réservé au dirigeant. Rien n’est appliqué d’office : choisissez un taux. La marge restante se calcule sur le coût total du devis
            (main-d’œuvre, matériel, sous-traitance, frais), comme l’onglet Rentabilité.
          </p>
          {e.remise_actuelle && (
            <div className="pile rp-actuelle">
              {/* Bouton sous le texte (pas en action de l'alerte) : sur téléphone, le texte garde toute la largeur. */}
              <Alerte ton="info" titre={`Remise appliquée : ${pctFr(e.remise_actuelle.pct)} ${ASSIETTE_PHRASE[e.remise_actuelle.assiette]}${e.remise_actuelle.montant ? ` (−${euros(e.remise_actuelle.montant)} HT)` : ''}`}>
                Visible de tous sur le devis et dans le PDF client, comme toute remise ; les marges restent ici.
              </Alerte>
              <div className="groupe-btn"><Bouton icone={<Undo2 aria-hidden />} chargement={retirer.isPending} onClick={() => retirer.mutate()}>Retirer la remise</Bouton></div>
            </div>
          )}
          <label className="case rp-assiette">
            <input type="checkbox" checked={choisie === 'devis'} disabled={e.nb_lignes_gamme === 0}
              onChange={(ev) => setAssiette(ev.target.checked ? 'devis' : 'ma_gamme')} />
            <span>Appliquer à tout le devis{e.nb_lignes_gamme === 0 ? ' (aucune ligne Ma gamme)' : ''}</span>
          </label>
          <dl className="rp-chiffres petit">
            <div><dt>Assiette</dt><dd>{choisie === 'ma_gamme' ? `${pluriel(e.nb_lignes_gamme, 'ligne Ma gamme', 'lignes Ma gamme')}` : 'Tout le devis'}{base ? ` — ${euros(base)} HT` : ''}</dd></div>
            <div><dt>Coût total du devis</dt><dd>{e.cout_total ? euros(e.cout_total) : 'Non calculable'}</dd></div>
            <div><dt>Marge sans remise</dt><dd>{e.marge_sans_remise ? `${euros(e.marge_sans_remise)}${e.marge_sans_remise_pct ? ` (${nombre(e.marge_sans_remise_pct, 1)} %)` : ''}` : 'Non calculable'}</dd></div>
          </dl>
          {e.non_calculable && <Alerte ton="ambre" titre="Aucune remise applicable pour l’instant">{e.non_calculable}</Alerte>}
          <ul className="rp-taux" aria-label="Taux proposés">
            {e.taux.map((t) => (
              <li key={t.pct}>
                <button type="button" className="rp-taux-btn" disabled={!t.autorise || appliquer.isPending} aria-describedby={t.motif ? `rp-motif-${t.pct}` : undefined}
                  onClick={() => appliquer.mutate(t.pct)} data-taux={t.pct}>
                  <strong className="rp-taux-pct">{pctFr(t.pct)}</strong>
                  {t.ht_net !== null && <span>Total HT après remise : <span className="chiffre">{euros(t.ht_net)}</span></span>}
                  {t.marge !== null && <span>Marge restante : <span className="chiffre">{euros(t.marge)}</span>{t.marge_pct ? ` (${nombre(t.marge_pct, 1)} %)` : ''}</span>}
                  {t.autorise ? <span className="tres-petit rp-taux-action">Appliquer {pctFr(t.pct)}</span>
                    : <span id={`rp-motif-${t.pct}`} className="tres-petit rp-taux-motif">{t.motif}</span>}
                </button>
              </li>
            ))}
          </ul>
          <form className="rp-saisie" onSubmit={(ev) => { ev.preventDefault(); if (saisieOk) appliquer.mutate(saisie.trim().replace(',', '.')); }}>
            <Champ libelle="Autre taux (%)" inputMode="decimal" value={saisie} onChange={(ev) => setSaisie(ev.target.value)} disabled={!!e.non_calculable}
              aide={`Plus de 0 et au plus ${TAUX_REMISE_MAX} %. Refusé s’il fait vendre sous le coût total ou sous le seuil de ${nombre(e.marge_min_pct, 2)} %.`} />
            <Bouton type="submit" icone={<BadgePercent aria-hidden />} chargement={appliquer.isPending} disabled={!saisieOk || !!e.non_calculable}>Appliquer ce taux</Bouton>
          </form>
          <ReglagesRemise />
        </div>
      )}
    </Panneau>
  );
}

/** Taux proposés et seuil de marge minimale : réglages de la société (« c'est un détail à préciser » : le dirigeant les fixe). */
function ReglagesRemise() {
  const qc = useQueryClient(); const notifier = useNotifier();
  const r = useQuery({ queryKey: ['remise-proposee-reglages'], queryFn: () => api.get<ReglagesRemiseProposee>('/api/devis/remise-proposee/reglages') });
  const [taux, setTaux] = useState(''); const [marge, setMarge] = useState('');
  useEffect(() => { if (r.data) { setTaux(r.data.taux.map((x) => x.replace('.', ',')).join(' ; ')); setMarge(r.data.marge_min_pct.replace('.', ',')); } }, [r.data]);
  const liste = lireListeTaux(taux);
  const margeOk = /^\d{1,2}([.,]\d{1,2})?$/.test(marge.trim()) && Number(marge.replace(',', '.')) <= MARGE_MIN_MAX;
  const enregistrer = useMutation({
    mutationFn: () => api.put<ReglagesRemiseProposee>('/api/devis/remise-proposee/reglages', { taux: liste, margeMinPct: marge.trim().replace(',', '.') }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['remise-proposee-reglages'] }); await qc.invalidateQueries({ queryKey: ['remise-proposee'] }); notifier('Taux proposés enregistrés pour la société.'); },
    onError: (err) => notifier(messageErreur(err), true),
  });
  if (!r.data) return null;
  return (
    <details className="rp-reglages">
      <summary className="petit"><Settings2 size={16} aria-hidden /> Régler les taux proposés</summary>
      <form className="pile" onSubmit={(ev) => { ev.preventDefault(); if (liste && margeOk) enregistrer.mutate(); }}>
        <Champ libelle="Taux proposés (%)" value={taux} onChange={(ev) => setTaux(ev.target.value)} inputMode="decimal"
          aide={`Séparés par « ; ». De ${TAUX_REMISE_MIN} à ${TAUX_REMISE_MAX} %, ${TAUX_REMISE_NB_MAX} au plus. Par défaut : ${r.data.defaut.taux.join(' ; ')}.`}
          erreur={taux && !liste ? `Taux de ${TAUX_REMISE_MIN} à ${TAUX_REMISE_MAX} %, séparés par « ; ».` : undefined} />
        <Champ libelle="Marge minimale (% du total HT après remise)" value={marge} onChange={(ev) => setMarge(ev.target.value)} inputMode="decimal"
          aide={`De 0 à ${MARGE_MIN_MAX} %. 0 = jamais sous le coût total. Par défaut : ${r.data.defaut.marge_min_pct} %.`}
          erreur={marge && !margeOk ? `Nombre de 0 à ${MARGE_MIN_MAX}.` : undefined} />
        <div className="groupe-btn"><Bouton type="submit" chargement={enregistrer.isPending} disabled={!liste || !margeOk}>Enregistrer les taux</Bouton></div>
      </form>
    </details>
  );
}
